import { useEffect, useState } from 'react';

interface EntryGateProps {
  onComplete: () => void;
}

export default function EntryGate({ onComplete }: EntryGateProps) {
  const [stage, setStage] = useState<'loading' | 'reveal' | 'complete'>('loading');
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + 2;
      });
    }, 25);

    const timer1 = setTimeout(() => {
      setStage('reveal');
    }, 2500);

    const timer2 = setTimeout(() => {
      setStage('complete');
      setTimeout(onComplete, 600);
    }, 3500);

    return () => {
      clearInterval(progressInterval);
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, [onComplete]);

  return (
    <div className={`fixed inset-0 z-[100] bg-[#0A0A0A] transition-opacity duration-800 ${
      stage === 'complete' ? 'opacity-0 pointer-events-none' : 'opacity-100'
    }`}>
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="w-full h-full" style={{
          backgroundImage: 'linear-gradient(#C9A86C 1px, transparent 1px), linear-gradient(90deg, #C9A86C 1px, transparent 1px)',
          backgroundSize: '40px 40px md:80px md:80px'
        }} />
      </div>

      {/* Reveal Curtains */}
      <div className={`absolute top-0 left-0 w-full h-1/2 bg-[#0A0A0A] z-20 transition-transform duration-1000 ease-in-out ${
        stage === 'reveal' || stage === 'complete' ? '-translate-y-full' : 'translate-y-0'
      }`}>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C9A86C] to-transparent" />
      </div>
      <div className={`absolute bottom-0 left-0 w-full h-1/2 bg-[#0A0A0A] z-20 transition-transform duration-1000 ease-in-out ${
        stage === 'reveal' || stage === 'complete' ? 'translate-y-full' : 'translate-y-0'
      }`}>
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C9A86C] to-transparent" />
      </div>

      {/* Center Content */}
      <div className={`absolute inset-0 flex items-center justify-center z-10 transition-opacity duration-500 ${
        stage === 'reveal' ? 'opacity-0' : 'opacity-100'
      }`}>
        <div className="text-center px-4 w-full max-w-md">
          {/* Logo */}
          <div className="mb-8 md:mb-12">
            <img 
              src="https://static.readdy.ai/image/f38dbee0b97d493b71475a98132a7b29/a415b53a125fde8d120dd58a802f1168.png" 
              alt="BuildFast - Nigerian Engineers. German Discipline." 
              className="h-12 sm:h-16 md:h-20 lg:h-24 w-auto object-contain mx-auto"
            />
          </div>

          {/* Tagline */}
          <p className="text-[#C9A86C] text-xs md:text-sm tracking-[0.3em] md:tracking-[0.4em] uppercase mb-10 md:mb-16">
            Luxury Construction Finishing
          </p>

          {/* Progress Bar */}
          <div className="w-48 sm:w-56 md:w-64 mx-auto">
            <div className="h-px bg-white/10 relative overflow-hidden">
              <div 
                className="absolute top-0 left-0 h-full bg-[#C9A86C] transition-all duration-100"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="flex justify-between mt-3 md:mt-4">
              <span className="text-white/30 text-[10px] md:text-xs tracking-widest">LOADING</span>
              <span className="text-[#C9A86C] text-[10px] md:text-xs font-medium">{progress}%</span>
            </div>
          </div>

          {/* Tagline */}
          <p className="text-white/40 text-xs md:text-sm mt-8 md:mt-12 tracking-wide">
            Crafting excellence in every detail
          </p>
        </div>
      </div>

      {/* Corner Decorations - Hidden on small screens */}
      <div className="absolute top-4 left-4 md:top-8 md:left-8 w-10 h-10 md:w-16 md:h-16 border-l border-t border-[#C9A86C]/20 hidden sm:block" />
      <div className="absolute top-4 right-4 md:top-8 md:right-8 w-10 h-10 md:w-16 md:h-16 border-r border-t border-[#C9A86C]/20 hidden sm:block" />
      <div className="absolute bottom-4 left-4 md:bottom-8 md:left-8 w-10 h-10 md:w-16 md:h-16 border-l border-b border-[#C9A86C]/20 hidden sm:block" />
      <div className="absolute bottom-4 right-4 md:bottom-8 md:right-8 w-10 h-10 md:w-16 md:h-16 border-r border-b border-[#C9A86C]/20 hidden sm:block" />
    </div>
  );
}
